/* eslint-disable camelcase */
import { sortBy, uniqBy } from 'lodash';
import moment from 'moment';
import URI from 'urijs';
import { constants } from '@clubmed/utils';
import { REMAINING_STOCK_LIMIT } from 'Components/utils/accommodation/accommodation.utils';
import { snakeToCamel } from 'Components/utils/string/string.util';
import { getAccommodationProps } from 'Components/widgets/accommodations/BookingAccommodationsUtils';
import { t } from 'Src/i18n';
import { API_AVAILABLE, API_DONE } from 'Utils/api-status/constants';
import { LEGAL_INFO_CGV_ID, HEALTH_REGULATION } from 'Utils/booking-process/constants';
import { API_DATE_FORMAT, momentFromApiDate } from 'Utils/date/apiDate';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import * as legacyBookingUrlBuilder from 'Utils/legacy-booking-engine/legacyBookingUrlBuilder';
import { STAFF_CHIEF_CODE } from 'Views/booking/constants';
import {
  QUERY_DATE_FORMAT,
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_NUMBER_OF_ADULTS,
  QUERY_TRANSPORT,
} from 'Utils/uri/constants';
import { serializeKidsToQueryParam } from 'Utils/faceting/facetingUtils';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import { getOptionByIdQuery } from 'Views/account/dashboard/DashboardView.queries';
import { CUSTOMER_ACCOUNT_GET_BOOKING_DATA } from 'Utils/request/requestDictionary';
const { BOOKING_STATUSES, PRODUCT_TYPES } = constants;
const { BOOKING_STATUS_EXPIRED_OPTION, BOOKING_STATUS_OPTION } = BOOKING_STATUSES;
export const isUpsellBooked = (
  upsell: (UpsellActivity | null | undefined) | (UpsellRental | null | undefined),
) =>
  Boolean(
    upsell?.pricingInfo?.isPartiallyRented ||
      upsell?.pricingInfo?.isPartiallyPaid ||
      upsell?.pricingInfo?.isFullyPaid,
  );
export const getFlatUpsells = (stay: StayEntity): Array<any> => {
  return Object.values(stay?.upsells || {}).reduce((acc, items) => [...acc, ...items], []);
};
export const hasSomeActivityBooked = (category: ActivitiesCategoryEntity) => {
  let hasSomeBooked = false;

  if (category.activities?.length) {
    hasSomeBooked = category.activities.some(({ additionalService }) =>
      isUpsellBooked(additionalService),
    );
  }

  if (category.categories?.length && !hasSomeBooked) {
    hasSomeBooked = category.categories.some((cat) => hasSomeActivityBooked(cat));
  }

  return hasSomeBooked;
};
export const getTransferFromAccessId = (
  transfers: TransportTransfer[],
  accessId: string,
): TransportTransfer | null | undefined =>
  transfers.find(
    (trans) =>
      trans.access?.id === accessId && Number(trans.duration) > 0 && !trans.id.includes('MICE_'),
  );
export const hasSomeUpsellsPartiallyRented = (upsells: Array<any>): boolean =>
  (upsells || []).some(({ pricingInfo = {} }) => pricingInfo.isPartiallyRented);
export const hasSomeUpsellsAlreadyPaid = (upsells: Array<any>): boolean =>
  (upsells || []).some(
    ({ pricingInfo = {} }) => pricingInfo.isFullyPaid || pricingInfo.isPartiallyPaid,
  );
export const hasSomeUpsellsInCartOrPaid = (upsells: Array<any>): boolean =>
  (upsells || []).some(
    ({ pricingInfo = {} }) =>
      pricingInfo.isPartiallyRented || pricingInfo.isPartiallyPaid || pricingInfo.isFullyPaid,
  );
export const hasAllUpsellsPaid = (upsells: Array<any>): boolean => {
  if (!upsells?.length) {
    return false;
  }

  return upsells.every(({ pricingInfo = {} }) => pricingInfo.isFullyPaid);
};
export const isEasyArrivalEnabled = (
  formalities: StayFormality | null | undefined,
  product: Product,
) => !product.isJoyview && [API_AVAILABLE, API_DONE].includes(formalities?.easyArrival?.status);
export const hasFreeDepositEnabled = (b: any, appContext: AppContext) =>
  isFeatureFlipped(appContext)('booking.banking.enableFreeDeposit') &&
  (b.paymentDetails?.duePayment?.amount?.value || 0) > 0 &&
  (appContext.freeDepositDeadline === 999
    ? moment(b.paymentDetails?.duePayment?.deadline, API_DATE_FORMAT).diff(moment()) > 0
    : moment(b.information?.stays?.[0]?.departureDate, API_DATE_FORMAT)
        .subtract(appContext.freeDepositDeadline, 'day')
        .diff(moment()) > 0);
export const isOptionBooking = (status?: string) =>
  [BOOKING_STATUS_OPTION, BOOKING_STATUS_EXPIRED_OPTION].includes(status);
export const doNotHaveValidStay = (booking: BookingEntity) =>
  booking &&
  ((booking?.stays || []).length === 0 || (booking?.stays || []).every((stay) => !stay.product));
export const getStayAccommodation = (
  accommodations: any,
  product: Product,
  streetViews: Array<StreetViewWithAccommodationCategoryId>,
): AccommodationEntity =>
  accommodations
    ? getAccommodationProps({
        accommodations: accommodations,
        accommodationsCount: accommodations.length || 0,
        attendeesWithDistributionData: [],
        product: product,
        proposal: null,
        remainingStock: REMAINING_STOCK_LIMIT + 1,
        streetViews: streetViews ?? [],
      })
    : {};
export const getCGVFromLegalInfos = (
  bookingProcess: ReadonlyArray<LegalInformation>,
): LegalInformation => {
  return (
    (bookingProcess || []).find((bp) => bp.id === LEGAL_INFO_CGV_ID) || {
      label: '',
    }
  );
};
export const getHealthRegulationsFromLegalInfos = (
  bookingProcess: ReadonlyArray<LegalInformation>,
): LegalInformation => {
  return (bookingProcess || []).find((bp) => bp.id === HEALTH_REGULATION) || null;
};
export const tAlt = ({
  cruiseKeyPath,
  product,
  resortKeyPath,
  variables,
}: {
  cruiseKeyPath: string;
  product: Product;
  resortKeyPath: string;
  variables?: Record<string, string | number>;
}) => {
  return t(product?.type === PRODUCT_TYPES.CRUISE ? cruiseKeyPath : resortKeyPath, variables);
};
export const getActivityTypeName = (type: string) =>
  snakeToCamel(type.toLowerCase().replace('rental', 'ski'));
export const getBookingEngineUrl = (
  appContext: AppContext,
  bookingId: string,
  productId: string,
  currentUri: string,
): string | null | undefined => {
  const params = {
    reference: bookingId,
    resortId: productId,
    fromSearches: 'true',
  };
  return legacyBookingUrlBuilder.buildUrlFromContextNumber(params, appContext, currentUri);
};
export const getShoppingUrl = (stay: Partial<StayEntity>): string => {
  const { dates, participants, product } = stay;
  const departureCity = stay.transport?.fromCityCode;
  return new URI(product.href).setQuery({
    ...{
      [QUERY_DEPARTURE_DATE]:
        dates.startDate && momentFromApiDate(dates.startDate).format(QUERY_DATE_FORMAT),
      [QUERY_DURATION]: momentFromApiDate(dates.endDate).diff(
        momentFromApiDate(dates.startDate),
        'days',
      ),
      [QUERY_KIDS]:
        (participants.children?.length > 0 &&
          serializeKidsToQueryParam({
            kids: participants.children,
            kidsValidationState: [],
          })) ||
        undefined,
      [QUERY_NUMBER_OF_ADULTS]: participants.adultsCount,
    },
    ...(!!departureCity && {
      [QUERY_TRANSPORT]: departureCity,
    }),
  });
};
export const getOptionById = (appContext: AppContext, bookingId: string) => {
  return graphqlClient.runNoCacheQuery(getOptionByIdQuery(bookingId, appContext), appContext, {
    requestName: CUSTOMER_ACCOUNT_GET_BOOKING_DATA,
    strictErrors: false,
  });
};
export const isOptionAboutToExpire = (optionDateTimeEnd?: Timestamp | null | undefined): boolean =>
  optionDateTimeEnd
    ? optionDateTimeEnd - 3600000 * 1 <=
      /* 1h */
      new Date().valueOf()
    : false;
export const getStaffChief = (staff: Array<StaffMember> = []) =>
  staff.find(({ categories = [] }) =>
    categories.some(({ code, label }) => [code, label].includes(STAFF_CHIEF_CODE)),
  ) || staff[0];
export const getStaffCategories = (staff: Array<StaffMember> = []) =>
  sortBy(
    uniqBy(
      staff
        .reduce((acc, s) => [...acc, ...s.categories], [])
        .map(({ code, label }) => ({
          label,
          value: code,
        })),
      'value',
    ),
    ['label'],
  );
export const hasStayTransport = (stay: StayEntity) => {
  const { inbounds = [], outbounds = [], type } = stay?.transport || {};
  const hasTransport = inbounds.length > 0 || outbounds.length > 0 || !!type;
  return hasTransport;
};
